import { useEffect, useRef, useState } from 'react';
import { createWithdrawalRequestId } from './walletWithdrawal';
import { pendingWalletKey, readPendingWallet, validWalletMoney, walletTransferOutcome,
    storedWalletOutcome, walletRequestRows } from './fastWalletTransfer';
import './VeritasonTransferModal.css';
import './RegisteredWallet.css';
import { sameTransferPolicy } from './registeredWalletPolicy';
import SecurityChallenge from '../../auth/SecurityChallenge';
import PinSecurity from '../../auth/PinSecurity';
const money = value => validWalletMoney(value) ? `${Number(value).toLocaleString('ko-KR')}원` : '-';

function SummaryRow({ label, children, total = false }) {
    return <div className={`registered-summary ${total ? 'registered-total' : ''}`}><span className="registered-summary-label">{label}</span><div className="registered-summary-value">{children}</div></div>;
}
// 01 registered-account V2: account resolution is exclusively server-side.
export default function FastTransferModal({ mid, currentBalance, withdrawable, withdrawalFee, registeredAccount,
    isOutsideWithdrawWindow, onTransfer, onRefresh, onQuery, onClose, policy, policyError, onReloadPolicy, onPolicy, onFunds, securityClient, onRestartLogin }) {
    const key = pendingWalletKey(process.env.REACT_APP_API, mid);
    const [currentPolicy, setCurrentPolicy] = useState(policy);
    const [proof, setProof] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [loginRequired, setLoginRequired] = useState(false);
    const [authBusy, setAuthBusy] = useState(false);
    const [input, setInput] = useState('');
    const [review, setReview] = useState(null);
    const [pending, setPending] = useState(null);
    const latestReview = useRef(review); latestReview.current = review;
    const [message, setMessage] = useState('');
    const [terminal, setTerminal] = useState(false);
    const [ready, setReady] = useState(false);
    const [busy, setBusy] = useState(false);
    const locked = useRef(false);
    const dialog = useRef(null);
    const alive = useRef(true);
    const epoch = useRef(0);
    const actions = useRef({ onQuery, onRefresh });
    actions.current = { onQuery, onRefresh };
    const amount = Number(input);
    const fundsReady = validWalletMoney(withdrawable) && validWalletMoney(withdrawalFee);
    const fee = Number(withdrawalFee);
    const max = fundsReady ? Math.max(0, Number(withdrawable) - fee) : 0;
    const accepts = currentPolicy?.supported === true && currentPolicy.accepting_requests === true;
    const needsRegistration = currentPolicy?.pin_required && currentPolicy.registration_required;
    const canReview = accepts && !needsRegistration && ready && !pending && !busy && fundsReady
        && Number.isSafeInteger(amount) && amount > 0 && amount <= max && !isOutsideWithdrawWindow;
    const changed = review && (!fundsReady || review.amount > max || review.fee !== fee
        || review.sourceAccount !== registeredAccount?.account || review.sourceBank !== registeredAccount?.bankCd);

    useEffect(() => {
        if (changed && !pending) { setProof(null); setReview(null); setMessage('계좌·잔액·수수료가 변경되었습니다. 송금 정보를 다시 확인해주세요.'); }
    }, [changed, pending]);
    const refreshBalances = () => Promise.resolve().then(() => actions.current.onRefresh()).catch(() => {});
    const savePending = value => { localStorage.setItem(key, JSON.stringify(value)); setPending(value); };
    const finish = outcome => {
        try { localStorage.removeItem(key); } catch { /* Keep confirmed outcome; a later restore can query this same request. */ }
        setPending(null); setReview(null); setProof(null); setInput(''); setReady(false); setTerminal(true);
        setMessage(outcome.message); void refreshBalances();
    };
    const fetchRecord = async snapshot => {
        const rows = walletRequestRows(await actions.current.onQuery({ request_id: snapshot.requestId }));
        const matches = rows.filter(row => row.request_id === snapshot.requestId);
        return matches.length === 1 ? matches[0] : undefined;
    };

    const synchronize = async () => {
        if (locked.current || authBusy) return;
        setProof(null); setReview(null);
        locked.current = true; setBusy(true); setReady(false);
        const version = ++epoch.current;
        try {
            const saved = readPendingWallet(key);
            if (saved) {
                setPending(saved); setReview(null);
                const record = await fetchRecord(saved);
                if (!alive.current || version !== epoch.current) return;
                const outcome = storedWalletOutcome(record);
                if (outcome.terminal) finish(outcome);
                else setMessage(record ? outcome.message : '요청을 아직 찾지 못했습니다. 같은 요청 번호를 유지하고 다시 조회해주세요.');
            } else {
                // Recover in-flight requests even if this browser has no local marker.
                const lists = await Promise.all(['VALIDATING', 'UNKNOWN', 'PENDING'].map(status =>
                    actions.current.onQuery({ status, limit: 100 }).then(walletRequestRows)));
                if (!alive.current || version !== epoch.current) return;
                const record = lists.flat().find(row => ['VALIDATING', 'UNKNOWN', 'PENDING'].includes(row.status));
                if (record) {
                    if (!record.request_id) throw new Error('진행 중인 요청 번호를 확인하지 못했습니다.');
                    savePending({ requestId: record.request_id });
                    setMessage(storedWalletOutcome(record).message);
                } else { setReady(true); setPending(null); setTerminal(false); setMessage(''); }
            }
            void refreshBalances();
        } catch (error) {
            if (alive.current && version === epoch.current) setMessage(error.message || '송금 요청 조회에 실패했습니다. 다시 확인해주세요.');
        } finally {
            if (version === epoch.current) { locked.current = false; if (alive.current) setBusy(false); }
        }
    };
    const syncRef = useRef(synchronize);
    syncRef.current = synchronize;
    useEffect(() => {
        alive.current = true; dialog.current?.focus();
        void syncRef.current();
        const sync = event => { if (event.key === key || event.key === null) void syncRef.current(); };
        window.addEventListener('storage', sync);
        const invalidateScope = () => { alive.current = false; ++epoch.current; locked.current = false; };
        return () => { invalidateScope(); window.removeEventListener('storage', sync); };
    }, [key]);

    const validateNewRequest = async () => {
        let next;
        try { next = await onPolicy(); } catch (e) { if (alive.current) { setCurrentPolicy(null); if ([4280, 9999].includes(e.code)) setLoginRequired(true); } throw e; }
        if (!alive.current) throw new Error('화면이 종료되었습니다.');
        setCurrentPolicy(next);
        if (!next.supported || !next.accepting_requests) throw new Error('현재 신규 출금을 접수하지 않습니다. 기존 요청 결과는 조회할 수 있습니다.');
        if (next.pin_required && next.registration_required) throw new Error('PIN 등록 후 다시 로그인해주세요.');
        const funds = await onFunds();
        return { policy: next, funds };
    };
    const lookup = async () => {
        if (!canReview || locked.current) return;
        locked.current = true; setBusy(true); setMessage(''); setReview(null); setProof(null);
        const version = epoch.current;
        try {
            const snapshot = await validateNewRequest();
            if ((registeredAccount?.account && (snapshot.funds.account !== registeredAccount.account || snapshot.funds.bank !== registeredAccount.bankCd)) || snapshot.funds.fee !== fee || snapshot.funds.available < amount + fee) throw new Error('잔액 또는 수수료가 변경되었습니다. 다시 확인해주세요.');
            if (alive.current && version === epoch.current) setReview({ amount, fee, requestId: createWithdrawalRequestId(),
                policy: snapshot.policy, funds: snapshot.funds,
                sourceAccount: registeredAccount?.account, sourceBank: registeredAccount?.bankCd,
                recipient: { bankCd: snapshot.funds.bank, account: snapshot.funds.account, bankName: snapshot.funds.bankName || registeredAccount?.bankName, accountName: snapshot.funds.accountName || registeredAccount?.accountName } });
        } catch (error) { if (alive.current && version === epoch.current) { setMessage(error.message || '출금 정보를 확인하지 못했습니다.'); void refreshBalances(); } }
        finally { if (alive.current && version === epoch.current) { locked.current = false; setBusy(false); } }
    };
    const submit = async () => {
        if (locked.current || pending || !review || changed || isOutsideWithdrawWindow || !ready || !accepts || authBusy) return;
        if (review.policy.pin_required && (!proof || proof.expiresAt <= Date.now())) { setProof(null); setReview(null); setMessage('송금 PIN 인증을 다시 진행해주세요.'); return; }
        locked.current = true; setBusy(true); setMessage('');
        const version = epoch.current;
        let snapshot;
        try {
            const fresh = await validateNewRequest();
            if (!alive.current || version !== epoch.current || latestReview.current !== review) return;
            if (!sameTransferPolicy(review.policy, fresh.policy) || fresh.funds.fee !== review.fee
                || fresh.funds.bank !== review.funds.bank || fresh.funds.account !== review.funds.account
                || fresh.funds.available < review.amount + review.fee) {
                setReview(null); setProof(null); setMessage('정책·등록계좌·잔액·수수료가 변경되었습니다. 송금 내용을 다시 확인하고 인증해주세요.'); void refreshBalances(); return;
            }
            if (review.policy.pin_required && proof.expiresAt <= Date.now()) { setReview(null); setProof(null); setMessage('송금 PIN 인증이 만료되었습니다. 다시 인증해주세요.'); return; }
        } catch (e) { setReview(null); setProof(null); setMessage(e.message || '출금 정보를 확인하지 못했습니다.'); return; }
        finally { locked.current = false; if (alive.current) setBusy(false); }
        locked.current = true; setBusy(true);
        try {
            const existing = readPendingWallet(key);
            if (existing) { setPending(existing); setReview(null); setReady(false); return; }
            snapshot = { requestId: review.requestId, amount: review.amount, fee: review.fee,
                bankName: review.recipient.bankName, accountLast4: review.recipient.account?.slice(-4), createdAt: new Date().toISOString() };
            savePending(snapshot); // No secret, token or full account is persisted.
            setReady(false);
        } catch { snapshot = null; setReady(false); setMessage('송금 요청을 저장하지 못해 신청을 중단했습니다.'); }
        finally { if (!snapshot) { locked.current = false; setBusy(false); } }
        if (!snapshot) return;
        try {
            const result = await onTransfer(snapshot.amount, snapshot.requestId, review.policy.pin_required ? proof.value : undefined);
            if (!alive.current || version !== epoch.current) return;
            if ([4280, 9999].includes(result?.code)) setLoginRequired(true);
            const outcome = walletTransferOutcome(result?.code);
            if (outcome.terminal) finish({ ...outcome, message: result?.code === 1002 ? result.msg || outcome.message : outcome.message });
            else {
                setMessage(({ 4281: 'PIN 등록이 필요합니다. 요청 결과를 확인한 후 등록해주세요.', 4100: '송금 인증이 만료되었거나 내용·정책이 변경되었습니다. 요청 결과를 확인해주세요.', 4280: '로그인 PIN 인증이 필요합니다. 다시 로그인해주세요.', 9999: '다시 로그인해주세요.' })[result?.code] || result?.msg || outcome.message);
                // Read the persisted outcome for validation rejections/conflicts. Never retry POST automatically.
                if (![1001, 1003].includes(result?.code)) {
                    const record = await fetchRecord(snapshot);
                    if (!alive.current || version !== epoch.current) return;
                    const stored = storedWalletOutcome(record);
                    if (stored.terminal) finish(stored);
                    else if (record) setMessage(stored.message);
                }
            }
        } catch { if (alive.current && version === epoch.current) setMessage('송금 결과를 확인하지 못했습니다. 동일 요청 번호로 결과를 조회해주세요.'); }
        finally { if (alive.current && version === epoch.current) { locked.current = false; setBusy(false); void refreshBalances(); } }
    };
    const close = () => { if (!locked.current && !authBusy) onClose(); };
    const shown = pending || review;
    if (registering) return <div className="pin-modal-backdrop"><div className="pin-dialog-shell" role="dialog" aria-modal="true" aria-label="송금 PIN 등록">
        <PinSecurity initialCode={4281} onCancel={() => setRegistering(false)} onRestart={onRestartLogin} />
    </div></div>;
    return <div className="modal-backdrop" onClick={close}>
        <div className="modal-sheet veritason-transfer registered-wallet" ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="fast-transfer-title" onClick={e => e.stopPropagation()} onKeyDown={e => {
            if (e.key === 'Escape') close();
            if (e.key === 'Tab') {
                const controls = [...dialog.current.querySelectorAll('button:not(:disabled),input:not(:disabled)')];
                const first = controls[0], last = controls[controls.length - 1];
                if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { e.preventDefault(); last?.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
            }
        }}>
            <div className="modal-head"><h2 id="fast-transfer-title">{pending ? '송금 결과 확인' : review ? '송금 정보 확인' : '이체'}</h2><button className="modal-close" onClick={close} disabled={busy || authBusy} aria-label="닫기">✕</button></div>
            <div className="modal-body">
                {loginRequired && <button className="btn btn-outline-secondary" disabled={busy} onClick={() => onRestartLogin('다시 로그인 후 송금 결과를 확인해주세요.')}>로그인 화면으로</button>}
                {policyError && <p role="alert">{policyError}</p>}
                {!accepts && <p role="status">신규 출금이 중단되었거나 출금 정책을 확인하지 못했습니다. 기존 요청 결과만 조회할 수 있습니다.</p>}
                {needsRegistration && !pending && <div><p>송금용 PIN 등록이 필요합니다. 등록 후 다시 로그인해주세요.</p><button className="btn btn-primary" disabled={busy || !ready} onClick={() => setRegistering(true)}>PIN 등록</button></div>}
                {message && <p role="status" className="veritason-transfer-intro">{message}</p>}
                {pending && <><p>확인 중인 요청이 있어 새 송금을 신청할 수 없습니다.</p><p>요청 번호: {pending.requestId}</p></>}
                {shown ? <>
                    <div className="row">받는 계좌: {pending ? `${pending.bankName || ''} ${pending.accountLast4 ? `끝자리 ${pending.accountLast4}` : '기존 요청 계좌'}` : `${review.recipient.bankName || ''} ${review.recipient.account || '서버에 등록된 계좌'}`}</div>
                    {!pending && review.recipient.accountName && <div className="row">예금주: {review.recipient.accountName}</div>}
                    <div className="row">송금액: {money(shown.amount)}</div>
                    <div className="row">수수료: {money(shown.fee)}</div>
                    <div className="row"><strong>총 차감액: {money(validWalletMoney(shown.amount) && validWalletMoney(shown.fee) ? Number(shown.amount) + Number(shown.fee) : null)}</strong></div>
                    {!pending && changed && <p role="alert">계좌·잔액·수수료가 변경되었습니다. 송금 정보를 다시 확인해주세요.</p>}
                </> : !terminal && <>
                    <SummaryRow label="현재 잔고"><strong>{money(currentBalance)}</strong></SummaryRow>
                    <SummaryRow label="출금가능 잔고"><strong>{fundsReady ? money(max) : '-'}</strong></SummaryRow>
                    <SummaryRow label="출금 수수료"><span className="registered-fee">{money(withdrawalFee)}</span></SummaryRow>
                    {!fundsReady && <p role="alert">잔액과 수수료 정보를 확인해야 송금할 수 있습니다.</p>}
                    <div className="registered-recipient">
                        <div className="registered-recipient-label">출금 계좌</div>
                        <div className="registered-account-card"><strong>등록 계좌</strong><div className="registered-account-detail">
                            {registeredAccount ? `${registeredAccount.bankName || ''} ${registeredAccount.account}` : '서버에 등록된 계좌로 송금됩니다.'}
                            {registeredAccount?.accountName && <span> ({registeredAccount.accountName})</span>}
                        </div></div>
                    </div>
                    <SummaryRow label={<label htmlFor="registered-transfer-amount">이체 금액</label>}>
                        <div className="amount-input-wrap"><input id="registered-transfer-amount" aria-label="송금 금액" inputMode="numeric" autoComplete="off" placeholder="0" className="amount-input" disabled={!ready || busy || !accepts || needsRegistration} value={input} onChange={e => setInput(e.target.value.replace(/\D/g, ''))} /><span>원</span><button className="btn-all" onClick={() => setInput(String(max))} disabled={!fundsReady || !ready || busy || !accepts || needsRegistration}>전액</button></div>
                    </SummaryRow>
                    <SummaryRow label="송금 후 출금가능 잔액" total><strong>{fundsReady && Number.isSafeInteger(amount) ? money(Math.max(0, max - amount)) : '-'}</strong></SummaryRow>
                    {amount > max && <p role="alert" className="field-error">출금가능 잔고를 초과했습니다.</p>}

                </>}
                {review && !pending && !changed && review.policy.pin_required && <section className="pin-ui">
                    <h3>송금 PIN 인증</h3>
                    {proof ? <p role="status">PIN 인증 완료. 송금 내용을 확인하고 신청해주세요.</p> : <SecurityChallenge key={review.requestId}
                        client={securityClient} purpose="withdraw" method="PIN" polished autoStart onBusyChange={setAuthBusy}
                        request={{ amount: review.amount, request_id: review.requestId }}
                        onVerified={setProof} onSessionError={e => { setCurrentPolicy(null); if ([4280, 9999].includes(e.code)) setLoginRequired(true); setReview(null); setProof(null); setMessage(e.message); }} />}
                </section>}
                {isOutsideWithdrawWindow && !pending && <p role="alert">출금 가능 시간이 아닙니다.</p>}
            </div>
            <div className="modal-foot">
                {pending || !ready ? <button className="btn-transfer" disabled={busy} onClick={synchronize}>{terminal ? '새 송금 준비' : '송금 결과 조회'}</button>
                    : review ? <><button disabled={busy || authBusy} onClick={() => { setReview(null); setProof(null); }}>수정</button><button className="btn-transfer" disabled={busy || authBusy || changed || isOutsideWithdrawWindow || !accepts || (review.policy.pin_required && !proof)} onClick={submit}>{busy ? '처리 중…' : '송금 신청'}</button></>
                    : <button className="btn-transfer" disabled={!canReview} onClick={lookup}>송금 정보 확인</button>}
                {!pending && (policyError || !accepts) && <button className="btn btn-outline-secondary" disabled={busy} onClick={onReloadPolicy}>출금 정책 다시 조회</button>}
            </div>
        </div>
    </div>;
}
